import React, { useState, useEffect } from "react";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../Layout/GRISidebar";
import styles from "../../../styles/gri/general.module.css";
import { Box, Button, Alert, Snackbar } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { put, get } from "../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import SectionHeading from "../GRI300/GRI308/SectionHeading";
import CustomTextField from "../GRI300/GRI308/CustomTextField";
import ReviewBox from "../../../Components/Common/ReviewBox";
import { currentYear } from "../../../utils/generateYearRanges";

const GRI407 = ({ hide }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [formData, setFormData] = useState({
    SignificantRiskIncidents: {
      incidents: "",
      childLabour: "",
      youngWorkers: "",
    },

    significantRiskChildLabors: {
      childLabors: "",
      operationTypeAndSupplier: "",
      countriesOrRegionsAtRisk: "",
    },

    measuresToAbolishChildLabor: "",
  });
  const [griReview, setGriReview] = useState(() => JSON.parse(localStorage.getItem("griReview")));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/gri/get?type=GRI400&Id=${id}`);
        if (response?.data?.data?.year < currentYear - 1)
          setDisabled(true);
        const data = response.data.data.GRI400?.childLabourpoints || {};
        setFormData({
          SignificantRiskIncidents: {
            incidents: data.SignificantRiskIncidents?.incidents || "",
            youngWorkers: data.SignificantRiskIncidents?.youngWorkers || "",
            childLabour: data.SignificantRiskIncidents?.childLabour || "",
          },
          significantRiskChildLabors: {
            childLabors: data.significantRiskChildLabors?.childLabors || "",
            operationTypeAndSupplier:
              data.significantRiskChildLabors?.operationTypeAndSupplier || "",
            countriesOrRegionsAtRisk:
              data.significantRiskChildLabors?.countriesOrRegionsAtRisk || "",
          },
          measuresToAbolishChildLabor: data.measuresToAbolishChildLabor || "",
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleNestedInputChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));

    // Remove error for the specific field
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    let newErrors = {};
    let formIsValid = true;

    // Validate sector

    // Validate required fields in the formData
    if (!formData.SignificantRiskIncidents.incidents) {
      newErrors.incidents = "This field is required";
      formIsValid = false;
    }

    if (!formData.SignificantRiskIncidents.childLabour) {
      newErrors.childLabour = "This field is required";
      formIsValid = false;
    }

    if (!formData.SignificantRiskIncidents.youngWorkers) {
      newErrors.youngWorkers = "This field is required";
      formIsValid = false;
    }

    if (!formData.significantRiskChildLabors.childLabors) {
      newErrors.childLabors = "This field is required";
      formIsValid = false;
    }

    if (!formData.significantRiskChildLabors.operationTypeAndSupplier) {
      newErrors.operationTypeAndSupplier = "This field is required";
      formIsValid = false;
    }

    if (!formData.significantRiskChildLabors.countriesOrRegionsAtRisk) {
      newErrors.countriesOrRegionsAtRisk = "This field is required";
      formIsValid = false;
    }

    if (!formData.measuresToAbolishChildLabor) {
      newErrors.measuresToAbolishChildLabor = "This field is required";
      formIsValid = false;
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      // if (!validateForm()) return;
      const payload = {
        type: "GRI400",
        submitted408: true,
        childLabourpoints: {
          ...formData,
        },
      };

      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        await put(`/gri/update?Id=${id}`, payload);
        navigate(`/gri/gri400/409-1/${id}`);
      } catch (error) {
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/gri400/409-1/${id}`);
    }
  };

  const renderContent = ({ hide }) => (
    <>
      <p className={styles.section_heading} style={{ marginBottom: hide ? "-1rem" : "0" }}>408 : Child Labour</p>
      <p className={styles.main_heading} style={{ marginBottom: hide ? "-1rem" : "0" }}>
        408-1 : Operations and incidents at significant risk for incidents of
        child labor
      </p>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {/* Section A */}

        <SectionHeading text="a. Identify operations and suppliers where there is a significant risk of incidents involving:" />
        <CustomTextField
          value={formData.SignificantRiskIncidents.incidents}
          onChange={(e) =>
            handleNestedInputChange(
              "SignificantRiskIncidents",
              "incidents",
              e.target.value
            )
          }
          error={errors.incidents}
          helperText={errors.incidents}
          disabled={disabled}
        />

        <Box sx={{ marginLeft: "1rem" }}>
          <SectionHeading text="i. Child Labor" />
          <CustomTextField
            value={formData.SignificantRiskIncidents.childLabour}
            onChange={(e) =>
              handleNestedInputChange(
                "SignificantRiskIncidents",
                "childLabour",
                e.target.value
              )
            }
            error={errors.childLabour}
            helperText={errors.childLabour}
            disabled={disabled}
          />

          <SectionHeading text="ii. Young workers exposed to hazardous work" />
          <CustomTextField
            value={formData.SignificantRiskIncidents.youngWorkers}
            onChange={(e) =>
              handleNestedInputChange(
                "SignificantRiskIncidents",
                "youngWorkers",
                e.target.value
              )
            }
            error={errors.youngWorkers}
            helperText={errors.youngWorkers}
            disabled={disabled}
          />
        </Box>

        <SectionHeading text="b. Identify operations and suppliers with a significant risk of incidents involving child labor? Please specify:" />
        <CustomTextField
          value={formData.significantRiskChildLabors.childLabors}
          onChange={(e) =>
            handleNestedInputChange(
              "significantRiskChildLabors",
              "childLabors",
              e.target.value
            )
          }
          error={errors.childLabors}
          helperText={errors.childLabors}
          disabled={disabled}
        />

        <Box sx={{ marginLeft: "1rem" }}>
          <SectionHeading text="i. The type of operation (e.g., manufacturing plant) and supplier." />
          <CustomTextField
            value={formData.significantRiskChildLabors.operationTypeAndSupplier}
            onChange={(e) =>
              handleNestedInputChange(
                "significantRiskChildLabors",
                "operationTypeAndSupplier",
                e.target.value
              )
            }
            error={errors.operationTypeAndSupplier}
            helperText={errors.operationTypeAndSupplier}
            disabled={disabled}
          />

          <SectionHeading text="ii. Countries or geographic areas where operations and suppliers are considered at risk." />
          <CustomTextField
            value={formData.significantRiskChildLabors.countriesOrRegionsAtRisk}
            onChange={(e) =>
              handleNestedInputChange(
                "significantRiskChildLabors",
                "countriesOrRegionsAtRisk",
                e.target.value
              )
            }
            error={errors.countriesOrRegionsAtRisk}
            helperText={errors.countriesOrRegionsAtRisk}
            disabled={disabled}
          />
        </Box>

        <SectionHeading
          text="c. What measures did the organization undertake during the reporting period to contribute to the effective abolition of child labor?"
          sx={{ marginTop: "2rem" }}
        />
        <CustomTextField
          value={formData.measuresToAbolishChildLabor}
          onChange={(e) =>
            handleInputChange("measuresToAbolishChildLabor", e.target.value)
          }
          error={errors.measuresToAbolishChildLabor}
          helperText={errors.measuresToAbolishChildLabor}
          disabled={disabled}
        />

        {/* Submit Button */}

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackOpen.open}
          message={snackOpen.message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackOpen({ ...snackOpen, open: false })}
        >
          <Alert
            severity={snackOpen.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackOpen.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );

  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ ml: "1rem", width: "98%" }}>
                <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                  onClick={handleSubmit}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default GRI407;
